import React, { useState, useEffect } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { CheckCircle, ArrowRight, Loader2, Sun, Moon, Github, Facebook, Linkedin, Check, Languages } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Toaster } from '../components/ui/toaster';
import { useTranslation } from 'react-i18next';

type MotionDivProps = HTMLMotionProps<"div"> & {
  className?: string;
};

const MotionDiv = motion.div as React.FC<MotionDivProps>;

const Logo = () => (
  <svg width="40" height="40" viewBox="0 0 338 331" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M278.501 59.3895C243.714 63.6184 229.798 65.4905 213.607 68.1199C182.155 73.2283 174.364 76.3932 182.02 80.9512C186.553 83.6513 196.145 86.2741 242.831 97.5808C282.852 107.274 317.136 116.148 328.195 119.678C330.471 120.404 332.508 120.829 332.723 120.622C333.604 119.769 326.606 102.174 321.067 91.3177C315.082 79.5871 308.187 68.8773 301.933 61.5983C297.926 56.9338 298.363 56.9754 278.501 59.3895Z" fill="currentColor"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M281.523 59.1195C252.306 62.6407 240.619 64.1995 227.021 66.3889C200.605 70.6425 194.062 73.2776 200.492 77.0729C204.299 79.3212 212.355 81.5051 251.565 90.9196C285.178 98.9906 313.972 106.38 323.259 109.319C325.171 109.924 329.704 111.225 329.704 110.98C329.704 110.149 324.977 98.9261 319.39 88.119C314.363 78.3515 306.455 67.0195 301.203 60.9587C297.837 57.0748 298.205 57.1094 281.523 59.1195Z" fill="currentColor"/>
    <path d="M15.8267 94.7162C41.0726 42.829 92.8031 6.78499 151.938 0.83779C190.447 -3.03529 231.106 6.64533 263.033 27.2885C273.71 34.1917 286.877 44.6654 286.634 46.0628C286.553 46.5367 276.79 47.6805 263.909 48.7255C212.03 52.9328 179.629 56.5091 151.509 61.1328C100.178 69.574 82.1982 74.5452 86.8233 79.0185C87.2995 79.479 102.92 84.8443 121.534 90.9403C169.143 106.532 174.756 108.516 193.281 116.3C219.267 127.22 227.714 134.46 220.369 139.517C213.356 144.348 215.574 143.798 90.4823 171.652C62.8271 177.809 32.0029 184.71 21.9843 186.987L1.79614 190.786C1.36638 189.123 0.953812 186.199 0.118868 175.693C-0.782507 145.895 3.34552 120.369 15.8267 94.7162Z" fill="currentColor"/>
    <path d="M337.87 189.123C230.037 229.571 169.693 250.983 58.0948 288.465C94.1882 317.617 117.848 328.487 171.122 330.031C255.785 330.031 326.696 270.592 337.87 189.123Z" fill="currentColor"/>
    <path d="M188.743 318.392C275.554 307.585 323.258 252.719 337.87 189.123C221.209 232.205 167.661 250.456 71.8473 283.893C116.606 310.982 142.207 320.963 188.743 318.392Z" fill="currentColor"/>
    <path d="M168.544 0.415771C199.916 0.415771 238.595 11.2228 257.075 24.9395L115.683 42.8127C74.8557 50.2945 72.2771 61.9328 71.8474 65.2581C71.4176 68.5833 71.8474 76.0651 85.5998 79.806L184.015 112.643C184.015 112.643 195.189 116.384 193.9 122.203C192.611 128.022 177.139 131.763 162.097 135.504L0.0771484 171.25C0.606341 112.223 19.4164 79.806 49.4998 48.6319C89.4676 11.2228 137.171 0.415771 168.544 0.415771Z" fill="currentColor"/>
  </svg>
);

const HurufLogo = () => (
  <svg width="160" height="75" viewBox="0 0 100 47" fill="none" xmlns="http://www.w3.org/2000/svg" className="mb-8">
    <path d="M33.8994 43.3057C33.8994 44.3437 33.8999 45.0107 35.1045 45.0107C36.3082 45.0106 36.3252 44.3356 36.3252 43.3057V40.2988H37.8271V44.04C37.827 45.5435 36.8696 46.3622 35.1045 46.3623C33.3474 46.3623 32.3976 45.5436 32.3975 44.04V40.2988H33.8994V43.3057ZM64.9111 43.3057C64.9111 44.3437 64.9117 45.0107 66.1162 45.0107C67.3211 45.0107 67.3369 44.3357 67.3369 43.3057V40.2988H68.8379V44.04C68.8377 45.5435 67.8815 46.3623 66.1162 46.3623C64.3591 46.3623 63.4103 45.5436 63.4102 44.04V40.2988H64.9111V43.3057ZM18.084 42.6885H20.6328V40.2988H22.1436V46.2529H20.6328V44.04H18.084V46.2529H16.583V40.2988H18.084V42.6885ZM50.6475 40.2988C51.9677 40.2988 53.2549 40.5695 53.2549 41.8779C53.2548 42.8319 52.636 43.2467 52.0332 43.3818H52.2402C53.04 43.3819 53.3202 43.8967 53.3203 44.4453V46.2529H51.8271V44.2334H49.5908V46.2529H48.082V40.2988H50.6475ZM83.417 41.6328H80.6045V43.0176H83.2607V44.3018H80.6045V46.2529H79.0947V40.2988H83.417V41.6328ZM49.5908 42.9004H50.6475C51.3159 42.9004 51.7606 42.8749 51.7607 42.2754C51.7607 41.6588 51.2827 41.6416 50.6475 41.6416H49.5908V42.9004ZM61.9043 34.9893H53.5254L63.4434 21.6865H71.2861L61.9043 34.9893ZM59.1436 21.6797L54.1387 28.3379L48.8779 34.9814H40.5L46.7109 27.8311C45.3592 27.3786 44.1277 26.5697 43.0166 25.4053C42.5883 24.9669 42.213 24.5153 41.9053 24.0498C40.7545 22.4334 40.1924 20.6247 40.1924 18.625V18.0898C40.1924 15.5147 41.1428 13.2541 43.0566 11.3086C44.9569 9.34999 47.1654 8.37696 49.6816 8.37695H59.1436V21.6797ZM27.7754 8.37695C30.2873 8.37706 32.4974 9.35329 34.4043 11.3037C36.3095 13.2558 37.2636 15.5179 37.2637 18.0889V21.0947H30.7646V18.2891C30.7646 17.3574 30.4394 16.5822 29.7891 15.9609C29.1401 15.3407 28.3603 15.0293 27.4502 15.0293C26.6264 15.0293 25.8901 15.3617 25.2402 16.0273C24.5901 16.692 24.2656 17.4473 24.2656 18.2891V21.6777H30.7393V28.3301H24.2656V28.3379H17.7666V28.3301H0L4.76953 21.6777H17.7666V18.0889C17.7666 15.4732 18.7191 13.2001 20.626 11.2705C22.5321 9.34133 24.7418 8.37695 27.2549 8.37695H27.7754ZM100 18.625C100 21.2412 99.0499 23.5158 97.1631 25.4473C95.2761 27.3652 93.0667 28.3379 90.5107 28.3379H70.3936L74.9336 21.708C79.9122 21.694 76.257 21.6797 76.9932 21.6797H90.3105C91.1804 21.6796 91.9298 21.3648 92.5586 20.7207C93.1745 20.0771 93.495 19.3102 93.4951 18.4199V15.2275H100V18.625ZM49.8691 15.0352C48.959 15.0352 48.2093 15.3638 47.5938 16.0215C46.9914 16.6927 46.6836 17.4874 46.6836 18.4189C46.6836 19.2682 47.0182 20.022 47.6602 20.6797C48.3159 21.351 49.0526 21.6797 49.8691 21.6797C50.3238 21.6932 50.8059 21.6797 51.3008 21.6797H52.6387V15.0352H49.8691ZM92.2539 8.37695C92.8212 8.37705 93.2811 8.84803 93.2812 9.42871V13.9697C93.2812 14.5505 92.8213 15.0214 92.2539 15.0215H81.4775C80.9101 15.0215 80.4502 14.5506 80.4502 13.9697V9.42871C80.4504 8.84799 80.9102 8.37697 81.4775 8.37695H92.2539ZM30.7578 1.37012C30.7578 1.87726 30.7711 2.37039 30.7578 2.83594C30.7578 3.67174 30.4361 4.42542 29.7803 5.09668C29.1378 5.75376 28.4019 6.09666 27.5723 6.09668C26.6621 6.09668 25.8853 5.78161 25.2295 5.16504C24.5875 4.53494 24.2666 3.7675 24.2666 2.83594V0H30.7578V1.37012Z" fill="currentColor" className="text-primary"/>
  </svg>
);

const SocialIcon: React.FC<{ href: string; icon: React.ReactNode }> = ({ href, icon }) => (
  <motion.a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-gray-600 hover:text-primary dark:text-gray-400 dark:hover:text-primary transition-colors duration-200"
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.95 }}
  >
    {icon}
  </motion.a>
);

const LanguageToggle: React.FC<{ currentLang: string; onToggle: () => void }> = ({ currentLang, onToggle }) => (
  <button
    onClick={onToggle}
    className="flex items-center gap-2 px-3 py-2 rounded-full bg-white dark:bg-gray-800 shadow-md hover:shadow-lg transition-all duration-200"
  >
    <Languages className="h-5 w-5 text-primary" />
    <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
      {currentLang === 'ar' ? 'العربية' : 'English'}
    </span>
  </button>
);

const WhitelistPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode !== null) {
      return savedMode === 'true';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [requireService, setRequireService] = useState(false);

  const baseUrl = process.env.REACT_APP_SABIL_API_URL;
  const { toast, showToast } = useToast();

  const getBaseLanguage = (locale: string) => {
    const match = locale.match(/^([a-z]{2,2})(-\w+)?$/);
    return match ? match[1] : 'ar'; // fallback to 'ar' if no match
  };

  const params = new URLSearchParams(window.location.search);
  const hasWhitelistParam = params.has('wasAlreadyWhitelisted');

  useEffect(() => {
    localStorage.setItem('darkMode', isDarkMode.toString());
    
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const fetchLoginUrl = async () => {
    try {
      const res = await fetch(`${baseUrl}/api/darb/assabil/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          origin: window.location.href,
          requireService: requireService,
          language: getBaseLanguage(i18n.language),
          theme: isDarkMode ? 'dark' : 'light'
        }),
      });
      const data = await res.json();
      if (data?.status && data?.data?.url) {
        return data.data.url;
      }
    } catch (e) {
      showToast({
        title: t('whitelist.errors.network'),
        description: t('whitelist.errors.network'),
      });
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('wasAlreadyWhitelisted') === 'true') {
      showToast({
        title: t('whitelist.toasts.alreadyWhitelisted.title'),
        description: t('whitelist.toasts.alreadyWhitelisted.description'),
      });
    } else if (params.get('wasAlreadyWhitelisted') === 'false') {
      showToast({
        title: t('whitelist.toasts.success.title'),
        description: t('whitelist.toasts.success.description'),
      });
    }
  }, [showToast, t]);

  const handleOAuthRedirect = () => {
    setIsLoading(true);
    fetchLoginUrl().then((loginUrl) => {
      if (loginUrl) {
        window.location.href = loginUrl;
      } else {
        showToast({
          title: t('whitelist.errors.noLoginUrl'),
          description: t('whitelist.errors.noLoginUrl'),
        });
      }
    }).catch(() => {
      showToast({
        title: t('whitelist.errors.network'),
        description: t('whitelist.errors.network'),
      });
    });
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  };

  return (
    <>
    <div className={`relative min-h-screen bg-gradient-to-b from-white to-gray-100 dark:from-gray-900 dark:to-gray-800 flex flex-col items-center justify-center p-4 ${i18n.language === 'ar' ? 'font-arabic' : ''} overflow-hidden`}>
      {/* Decorative Circles */}
      <div 
        className="absolute -left-40 -bottom-40 w-[40rem] h-[40rem] rounded-full opacity-30 dark:opacity-20 z-10"
        style={{
          background: 'radial-gradient(circle at center, #ef4f00 0%, #ff8f4f 100%)',
          filter: 'blur(60px)',
          transform: 'rotate(-45deg)',
        }}
      />
      <div 
        className="absolute left-[12%] -bottom-[-22%] w-[20rem] h-[20rem] rounded-full opacity-30 dark:opacity-20 z-10"
        style={{
          background: 'radial-gradient(circle at center, #ef4f00 0%, #ff8f4f 100%)',
          filter: 'blur(60px)',
          transform: 'rotate(-45deg)',
        }}
      />

      <Toaster toast={toast} />

      {/* Fixed Toggle Bar at Top */}
      <div
        className="fixed top-0 left-0 w-full flex justify-end items-center gap-2 p-2 z-30 bg-transparent"
      >
        <LanguageToggle 
          currentLang={i18n.language} 
          onToggle={toggleLanguage}
        />
        <div
          onClick={() => setIsDarkMode(!isDarkMode)}
          className={`w-16 h-8 flex items-center cursor-pointer rounded-full px-1 transition-colors duration-300 relative ${isDarkMode ? 'bg-primary' : 'bg-gray-300'}`}
          style={{ boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)' }}
        >
          <span className="absolute left-2 top-1/2 -translate-y-1/2">
            <Sun className={`h-5 w-5 ${isDarkMode ? 'text-white' : 'text-primary'} transition-colors duration-300`} />
          </span>
          <span className="absolute right-2 top-1/2 -translate-y-1/2">
            <Moon className="h-5 w-5 text-primary transition-colors duration-300" />
          </span>
          <div
            style={{ left: isDarkMode ? 36 : 4 }}
            className="w-6 h-6 bg-white rounded-full shadow-md transform transition-all duration-500 ease-in-out flex items-center justify-center absolute top-1/2 -translate-y-1/2"
          />
        </div>
      </div>

      {/* Add top margin to main content to avoid overlap */}
      <div className="mt-16 w-full max-w-md relative z-20">
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
          style={{ zIndex: 1 }}
        >
          <MotionDiv
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
            }}
            className="flex justify-center mb-8"
          >
            <a href="https://huruftech.com" target="_blank" rel="noopener noreferrer">
              <HurufLogo />
            </a>
          </MotionDiv>

          <Card className="shadow-lg dark:bg-gray-800">
            <CardHeader className="space-y-1">
              <div className="flex items-center justify-center mb-4 gap-4">
                <MotionDiv
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 260,
                    damping: 20,
                    delay: 0.2,
                  }}
                  className="w-16 h-16 bg-[#db2525] rounded-full flex items-center justify-center text-white cursor-pointer"
                >
                  <a href="https://app.sabil.ly" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full h-full">
                    <Logo />
                  </a>
                </MotionDiv>
                <a href="https://wordpress.org" target="_blank" rel="noopener noreferrer" className="w-16 h-16 bg-[#21759c] rounded-full flex items-center justify-center hover:bg-[#1d6589] transition-colors duration-200">
                  <img src="/wordpress-logo.png" alt="WordPress" className="w-11 h-11" />
                </a>
              </div>
              <CardTitle className="text-2xl text-center font-bold dark:text-white">
                {t('whitelist.title')}
              </CardTitle>
              <CardDescription className="text-center dark:text-gray-400">
                {t('whitelist.subtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <MotionDiv
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="space-y-4"
              >
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600 dark:text-gray-300">{t('whitelist.features.earlyAccess')}</p>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600 dark:text-gray-300">{t('whitelist.features.discount')}</p>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600 dark:text-gray-300">{t('whitelist.features.support')}</p>
                </div>

                <div className="flex items-start space-x-3 pt-4">
                  <div className="relative flex items-start">
                    <input
                      type="checkbox"
                      id="requireService"
                      checked={requireService}
                      onChange={(e) => setRequireService(e.target.checked)}
                      className="peer sr-only"
                    />
                    <div 
                      className="h-5 w-5 rounded border-2 border-gray-300 bg-white peer-checked:border-primary peer-checked:bg-primary dark:border-gray-600 dark:bg-gray-700 dark:peer-checked:border-primary dark:peer-checked:bg-primary transition-all duration-200 cursor-pointer"
                      onClick={() => setRequireService(!requireService)}
                    >
                      <Check 
                        className={`h-4 w-4 text-white absolute top-0.5 left-0.5 transition-opacity duration-200 ${
                          requireService ? 'opacity-100' : 'opacity-0'
                        }`}
                        strokeWidth={3}
                      />
                    </div>
                  </div>
                  <label
                    htmlFor="requireService"
                    className="text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-100 transition-colors duration-200"
                  >
                    {t('whitelist.serviceCheckbox')}
                  </label>
                </div>
              </MotionDiv>
            </CardContent>
            <CardFooter>
              <MotionDiv 
                className="w-full"
                whileHover={{ scale: 1.02 }} 
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  className="w-full bg-primary hover:bg-primary/90 text-white disabled:bg-primary/40 disabled:hover:bg-primary/40 disabled:cursor-not-allowed"
                  onClick={handleOAuthRedirect}
                  disabled={isLoading || hasWhitelistParam}
                >
                  {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowRight className="mr-2 h-4 w-4" />
                  )}
                  {isLoading ? t('whitelist.connecting') : t('whitelist.joinButton')}
                </Button>
              </MotionDiv>
            </CardFooter>
          </Card>

          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="mt-8 space-y-6"
          >
            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              {t('whitelist.footer')}{' '}
              <a 
                href="https://huruftech.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/90 hover:underline dark:text-primary dark:hover:text-primary/90"
              >
                Huruf Tech
              </a>
            </p>

            <div className="flex items-center justify-center space-x-6">
              <SocialIcon
                href="https://github.com/Huruf-Tech/"
                icon={<Github size={24} />}
              />
              <SocialIcon
                href="https://www.facebook.com/huruftech/"
                icon={<Facebook size={24} />}
              />
              <SocialIcon
                href="https://www.linkedin.com/company/huruf-tech/"
                icon={<Linkedin size={24} />}
              />
            </div>
          </MotionDiv>
        </MotionDiv>
      </div>
    </div>
    </>

  );
};

export default WhitelistPage; 
